import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import {
  PDFDownloadLink,
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Font,
} from '@react-pdf/renderer';
import sanitizeHtml from 'sanitize-html';
import { Parser } from 'htmlparser2';

import { API_URL } from '..';

// Register Montserrat font for PDF
Font.register({
  family: 'Helvetica',
  src: '/assets/fonts/Helvetica.otf',
});

// Define styles for the PDF
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 12,
    fontFamily: 'Helvetica',
    color: '#000',
    lineHeight: 1.5,
    // fontFamily: 'Montserrat',
  },
  
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 50,
  },
  companyInfo: {
    width: '50%',
  },
  logo: {
    width: 90,
    marginBottom: 5,
  },
  invoiceTitle: {
    fontSize: 18,
    fontWeight: 600,
    paddingBottom: 30,
    textAlign: 'center',
    marginVertical: 10,
    borderBottom: 3,
    borderBottomColor: '#d17f15',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: 15,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#d17f15',
    color: '#fff',
    // padding: 8,
    fontSize: 12,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: 1,
    borderColor: '#c0c0c0',
    padding: 5,
    borderStyle: 'dotted'
  },
  tableColDesc: {
    width: '70%',
    fontSize: 9,
    borderRight: 1,
    borderColor: '#c0c0c0',
    borderStyle: 'dotted'
  },
  tableColQty: {
    width: '10%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 9,
    borderRight: 1,
    borderColor: '#c0c0c0',
    borderStyle: 'dotted'
  },
  tableColUnitPrice: {
    width: '10%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 9,
    borderRight: 1,
    borderColor: '#c0c0c0',
    borderStyle: 'dotted'
  },
  tableColPrice: {
    width: '10%',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 9,
  },
  paymentSection: {
    flexDirection: 'row',
    marginTop: 15,
  },
  paymentInstructions: {
    width: '50%',
    backgroundColor: '#f2f2f2',
    borderWidth: 2,
    borderColor: '#d17f15',
    textAlign: 'center',
    padding: 5,
  },
  totals: {
    width: '50%',
    textAlign: 'right',
  },
  totalRow: {
    padding: 5,
    fontSize: 12,
    backgroundColor: '#f3f3f3',
  },
  totalRowBold: {
    padding: 5,
    fontSize: 13,
    fontWeight: 700,
  },
  divider: {
    borderBottom: 1,
    borderColor: '#d17f15',
    marginVertical: 5,
  },
  termsSection: {
    marginTop: 15,
    fontSize: 12,
    textAlign: 'center',
  },
  footerLogos: {
    flexDirection: 'row',
    justifyContent: 'center',
    columnGap: 8,
    borderTop: 1,
    borderColor: '#c0c0c0',
    paddingTop: 15,
    marginTop: 40,
  },
  footerLogo: {
    objectFit: 'contain'
  },
  descriptionParagraph: {
    marginBottom: 0,
    fontSize: 7,
    lineHeight: 1.8,
  },
  descriptionBold: {
    fontWeight: 700,
    fontSize: 7,
    lineHeight: 1.8,
  },
  listItem: {
    marginLeft: 5,
    marginBottom: 3,
    fontSize:7
  },
});

// Helper: Convert image URL to base64
const toBase64 = async (url) => {
  try {
    const response = await fetch(`${API_URL}/proxy-image?url=${encodeURIComponent(url)}`, {
      mode: 'cors',
    });
    if (!response.ok) throw new Error(`Failed to fetch image: ${response.statusText}`);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to convert image to base64:', url, error);
    return url;
  }
};

// Helper: Parse HTML for PDF rendering
const parseHtmlToPdf = (html) => {
  if (!html) return <Text style={styles.descriptionParagraph}>N/A</Text>;
  const els = [];
  let txt = '';
  const parser = new Parser(
    {
      onopentag(name) {
        if (name === 'p' && txt) {
          els.push(<Text style={styles.descriptionParagraph}>{txt}</Text>);
          txt = '';
        }
      },
      ontext(t) {
        txt += t.trim() + ' ';
      },
      onclosetag(name) {
        if (['p', 'li', 'strong'].includes(name) && txt.trim()) {
          els.push(<Text style={styles.descriptionParagraph}>{txt}</Text>);
          txt = '';
        }
      },
    },
    { decodeEntities: true }
  );
  parser.write(
    sanitizeHtml(html, { allowedTags: ['p', 'ul', 'ol', 'li', 'strong', 'br'] })
  );
  parser.end();
  return <View>{els}</View>;
};

// Invoice PDF Component
const InvoicePDF = ({ data, showLogo, showAddress, showPhone, showVAT, showDescription, showTerms, logoBase64, footerLogosBase64 }) => {
  const calculateSubtotal = () => {
    return data?.items?.reduce((acc, item) => acc + parseFloat(item.price || 0), 0) || 0;
  };
  

  const calculateDiscountedTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = parseFloat(data?.discount) || 0;
    const discountAmount = (subtotal * discount) / 100;
    return subtotal - discountAmount;
  };

  const calculateTax = () => {
    const discountedTotal = calculateDiscountedTotal();
    const taxAmount = parseFloat(data?.tax) || 0;
    return (discountedTotal * taxAmount) / 100;
  };

  const calculateTotal = () => {
    return calculateDiscountedTotal() + calculateTax();
  };

  const convertDateFormat = (dateString) => {
    if (dateString) {
      const [year, month, day] = dateString.split('-');
      return `${day}-${month}-${year}`;
    }
    return '';
  };

  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View style={styles.header} fixed>
          <View style={styles.companyInfo}>
            {showLogo && logoBase64 && (
              <Image style={styles.logo} src={logoBase64} />
            )}
            <Text style={{ fontSize: 11, fontWeight: 'bold' }}>{data?.company?.name || 'R-Wire Limited'}</Text>
            {showAddress && <Text style={{ fontSize: 8, width: 125, lineHeight: 1.3, color: '#777474' }}>{data?.company?.address}</Text>}
            {showAddress && <Text style={{ fontSize: 8, width: 140, marginTop: 10, color: '#535151' }}>VAT reg no: {data?.company?.vat}</Text>}
            {showPhone && (
              <Text style={{ fontSize: 8, marginTop: 0, color: '#535151' }}>
                Email:
                <Text style={{ textDecoration: 'underline', fontSize: 8, marginTop: 0, color: '#535151' }}>
                  {data?.company?.email || ''}
                </Text>
              </Text>
            )}
          </View>
          <View>
            <Text style={{ fontSize: 8, color: '#535151' }}>Issue Date: {convertDateFormat(data?.issue_date)}</Text>
          </View>
        </View>
        <View style={styles.invoiceTitle}>
          <Text>INVOICE</Text>
        </View>
        <View style={styles.table}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <View style={{ width: '50%' }}>
              <Text style={{ fontWeight: 'bold', fontSize: 9 }}>Bill To:</Text>
              <Text style={{ fontSize: 8, color: '#000' }}>{data?.customer_name || 'Akira'}</Text>
              <Text style={{ fontSize: 7, color: '#535151' }}>{data?.phone || '07805410487'}</Text>
              <Text style={{ fontSize: 7, width: 140, color: '#535151' }}>{data?.address || '9-13 summertimee av'}</Text>
            </View>
            <View style={{ width: '50%', textAlign: 'right' }}>
              <Text style={{ fontSize: 7, color: '#535151' }}><Text style={{ fontWeight: 'bold' }}>Invoice No</Text>: {data?.invoice_number || 'RW-058'}</Text>
              {data?.order_number && <Text style={{ fontSize: 7, color: '#535151' }}><Text style={{ fontWeight: 'bold' }}>Order No</Text>: {data.order_number}</Text>}
              {data?.job_site_address && <Text style={{ fontSize: 7, color: '#535151' }}><Text style={{ fontWeight: 'bold' }}>Site Address</Text>: {data?.job_site_address}</Text>}
            </View>
          </View>
        </View>
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableColDesc, { fontWeight: 'bold', fontSize: 9,padding:1 }]}>Description</Text>
            <Text style={[styles.tableColQty, { fontWeight: 'bold', fontSize: 9,padding:1}]}>Quantity</Text>
            <Text style={[styles.tableColUnitPrice, { fontWeight: 'bold', fontSize: 9,padding:1 }]}>Unit Price</Text>
            <Text style={[styles.tableColPrice, { fontWeight: 'bold', fontSize: 9,padding:1 }]}>Cost</Text>
          </View>
          {data?.items?.filter(item => item.item_name && item.price).map((item, index) => (
            <View
              key={index}
              style={{
                ...styles.tableRow,
                backgroundColor: index % 2 === 0 ? '#ffffff' : '#f3f3f3',
              }}
            >
              <View style={styles.tableColDesc}>
                <Text>{item.item_name || 'N/A'}</Text>
                {showDescription && (
                  <View style={{ marginTop: 0 }}>
                    {parseHtmlToPdf(item.description || 'N/A')}
                  </View>
                )}
              </View>
              <Text style={[styles.tableColQty,{ paddingTop:item.description ? 20 : 0,fontSize: 9}]}>{item.quantity || 1}</Text>
              <Text style={[styles.tableColUnitPrice,,{paddingTop:item.description ? 20 : 0,fontSize: 9}]}>£{parseFloat(item.unit_price || 0).toFixed(2)}</Text>
              <Text style={[styles.tableColPrice,,{paddingTop:item.description ? 20 : 0,fontSize: 9}]}>£{parseFloat(item.price || 0).toFixed(2)}</Text>
            </View>
          ))}
        </View>
        <View style={styles.paymentSection}>
          <View style={styles.paymentInstructions}>
            <Text style={{ fontWeight: 'bold', fontSize: 10 }}>Payment Instructions</Text>
            <Text style={{ fontSize: 7, marginBottom: -4 }}>{data?.company?.accountTitle || 'R-Wire Limited'}</Text>
            <Text style={{ fontSize: 7, marginBottom: -4 }}>Sort code: {data?.company?.sortCode || '09-01-29'}</Text>
            <Text style={{ fontSize: 7, marginBottom: -4 }}>Account no: {data?.company?.accountNumber || '35103808'}</Text>
          </View>
          <View style={styles.totals}>
                  <Text style={[styles.totalRow,{fontSize: 9}]}>Total Net Amount: £{calculateSubtotal().toFixed(2)}</Text>
            
            {data?.discount > 0 && (
              <Text style={[styles.totalRow, { fontSize: 9 }]}>
                Discount: £{((calculateSubtotal() * (parseFloat(data?.discount) || 0)) / 100).toFixed(2)}
              </Text>
            )}
            {data?.tax > 0 && (
              <Text style={[styles.totalRow, { fontSize: 9 }]}>Total VAT Amount: £{calculateTax().toFixed(2)}</Text>
            )}
            <View style={styles.divider} />
            <Text style={[styles.totalRowBold, { fontSize: 9 }]}>Invoice Total: £{calculateTotal().toFixed(2)}</Text>
            <View style={styles.divider} />
          </View>
        </View>
        {showTerms && (
          <View style={styles.termsSection} break>
            <Text style={{ fontWeight: 'bold', fontSize: 9 }}>Terms and Conditions:</Text>
            <Text style={{ marginTop: 8, fontSize: 7, color: '#535151' }}>
              <Text style={{ fontWeight: 'bold', color: '#000' }}>Payment Due Date:</Text> Full payment is due within 14 days from the invoice date.
            </Text>
            <Text style={{ fontSize: 7, color: '#535151', lineHeight: 1.6 }}>
              <Text style={{ fontWeight: 'bold', color: '#000' }}>Late Payments:</Text> A late fee of 5% of the total invoice amount will be applied to any payment not received by the due date. Interest on overdue balances will accrue at 5% per week until payment is received in full.
            </Text>
            <Text style={{ fontSize: 7, color: '#535151', lineHeight: 1.6 }}>
              <Text style={{ fontWeight: 'bold', color: '#000' }}>Methods of Payment:</Text> Payment can be made via bank transfer. Please ensure the invoice number is included in all payments for proper processing.
            </Text>
            <Text style={{ fontSize: 7, color: '#535151', lineHeight: 1.6 }}>
              <Text style={{ fontWeight: 'bold', color: '#000' }}>Disputes:</Text> Any disputes regarding the invoice must be reported in writing within 7 days of the invoice date. Disputing any portion of the invoice does not suspend the obligation to pay the undisputed amount by the due date.
            </Text>
            <Text style={{ fontSize: 7, color: '#535151', lineHeight: 1.6 }}>
              <Text style={{ fontWeight: 'bold', color: '#000' }}>Suspension of Service:</Text> Non-payment may result in suspension of service or further legal action, at the discretion of R-Wire Limited.
            </Text>
            <Text style={{ fontWeight: 'bold', fontSize: 9, marginTop: 15 }}>Disclaimer:</Text>
            <Text style={{ fontSize: 7, color: '#535151', lineHeight: 1.6 }}>
              If payment of invoices is not received, we reserve the right to recover any cost, commissions or fees incurred for the collection of payments including those which may be incurred by using a debt collection agency. There will be a 5% late payment fee applied every 7 days after the due date of the invoice.
            </Text>
            <Text style={{ fontWeight: 'bold', fontSize: 8, marginTop: 15 }}>
              {data?.company?.name || 'R-Wire Limited'}
            </Text>
            <Text style={{ fontSize: 7 }}>Tel: {data?.company?.phone || '07950861019 / 07300400766'}</Text>
            <Text style={{ fontSize: 7 }}>{convertDateFormat(data?.issue_date) || '2025-06-27'}</Text>
          </View>
        )}
        <View fixed style={styles.footerLogos}>
          {footerLogosBase64.main && 
            <View style={{ width: '90%' }}>
              <Image style={styles.footerLogo} src={footerLogosBase64.main} />
            </View>
          }
        </View>
      </Page>
    </Document>
  );
};

// Main Invoice Preview Component
const InvoicePreviewLayer = ({ data }) => {
  const [spinner, setSpinner] = useState(false);
  const [message, setMessage] = useState({
    status: false,
    reason: '',
    desc: '',
  });
  const [showModal, setShowModal] = useState(false);
  const [sendMethod, setSendMethod] = useState('email');
  const [sendValue, setSendValue] = useState('');
  const [showLogo, setShowLogo] = useState(true);
  const [showAddress, setShowAddress] = useState(true);
  const [showPhone, setShowPhone] = useState(true);
  const [showVAT, setShowVAT] = useState(true);
  const [showDescription, setShowDescription] = useState(true);
  const [showTerms, setShowTerms] = useState(true);
  const [logoBase64, setLogoBase64] = useState(null);
  const [footerLogosBase64, setFooterLogosBase64] = useState({
    main: null,
    child: null,
    mini: null,
    trustmark: null,
    last: null,
  });
  const [imagesLoading, setImagesLoading] = useState(true);

  // Fetch and convert images to base64 on component mount
  useEffect(() => {
    const loadImages = async () => {
      setImagesLoading(true);
      try {
        // Convert company logo
        let logo = null;
        if (data?.company?.logo) {
          logo = await toBase64(`${API_URL}${data.company.logo}`);
        }

        // Convert footer logos
        const footerLogoUrls = {
          main: 'https://api.test.frontforcecrm.com/logo/icos.png',
        };

        const footerLogos = {};
        for (const [key, url] of Object.entries(footerLogoUrls)) {
          footerLogos[key] = await toBase64(url);
        }

        setLogoBase64(logo);
        setFooterLogosBase64(footerLogos);
      } catch (error) {
        console.error('Error loading images:', error);
      } finally {
        setImagesLoading(false);
      }
    };

    loadImages();
  }, [data]);

  // Handle sending invoice
  const handleSendInvoiceClick = (e) => {
    e.preventDefault();
    setShowModal(true);
  };

  const handleSend = async () => {
    try {
      const payload = {
        invoiceId: data?.id,
        method: sendMethod,
        value: sendValue,
      };

      const response = await fetch(`${API_URL}/api/invoice/send-invoice`, {
        method: 'POST',
        headers: {
          Accept: '*/*',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        setMessage({
          status: true,
          reason: 'Success',
          desc: `${sendMethod === 'email' ? 'Email' : 'SMS'} sent successfully!`,
        });
      } else {
        throw new Error(result.message || 'Failed to send');
      }
    } catch (error) {
      setMessage({
        status: false,
        reason: 'Error',
        desc: error.message,
      });
    }

    setShowModal(false);
    setSendValue('');
  };

  // Calculate totals for HTML preview
  const calculateSubtotal = () => {
    return data?.items?.reduce((acc, item) => acc + parseFloat(item.price || 0), 0) || 0;
  };

  const calculateDiscountedTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = parseFloat(data?.discount) || 0;
    const discountAmount = (subtotal * discount) / 100;
    return subtotal - discountAmount;
  };

  const calculateTax = () => {
    const discountedTotal = calculateDiscountedTotal();
    const taxAmount = parseFloat(data?.tax) || 0;
    return (discountedTotal * taxAmount) / 100;
  };

  const calculateTotal = () => {
    return calculateDiscountedTotal() + calculateTax();
  };

  const convertDateFormat = (dateString) => {
    if (dateString) {
      const [year, month, day] = dateString.split('-');
      return `${day}-${month}-${year}`;
    }
    return '';
  };

  // HTML Preview Component
  const renderHeader = () => (
    <table style={{ width: '100%' }}>
      <tbody>
        <tr>
          <td style={{ width: '50%', verticalAlign: 'top' }}>
            {showLogo && logoBase64 && (
              <img src={logoBase64} style={{ width: '100px' }} alt="Company Logo" />
            )}
            <div>{data?.company?.name || 'R-Wire Limited'}</div>
            {showAddress && <div style={{width: 140,color:'#777474'}}>{data?.company?.address}</div>}
            {showAddress && <div style={{color:'#535151'}}>VAT reg no :{data?.company?.vat}</div>}
            {showPhone && (
              <div>
                <a  href={`mailto:${data?.company?.email}`} style={{ textDecoration: 'underline',color:'#535151' }}>
                  {data?.company?.email}
                </a>
              </div>
            )}
          </td>
          <td style={{ width: '50%', textAlign: 'right',fontSize: 10 }}>
            <div>Issue Date: {convertDateFormat(data?.issue_date)}</div>
          </td>
        </tr>
      </tbody>
    </table>
  );

  const renderInvoiceTemplate = () => (
    <div style={{ fontSize: '12px', color: '#000' ,padding:20}}>
      {renderHeader()}
      <div style={{ borderBottom: '3px solid #d17f15', marginTop: '10px' }}>
        <p style={{ fontWeight: 600, fontSize: '18px', textAlign: 'center', margin: '10px 0 30px 0' }}>
          INVOICE
        </p>
      </div>
      <table style={{ width: '100%', marginTop: '10px' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top' }}>
              <div style={{ fontWeight: 'bold', fontSize: '13px' }}>Bill To:</div>
              <div style={{ fontSize: '12px' }}>{data?.customer_name || 'Akira'}</div>
              <div style={{ fontSize: '12px' }}>{data?.phone || '07805410487'}</div>
              <div style={{ fontSize: '12px', width: '140px' }}>{data?.address || '9-13 summertimee av'}</div>
            </td>
            <td style={{ width: '50%', textAlign: 'right' }}>
              <div style={{ fontSize: '12px' }}>Invoice No: {data?.invoice_number || 'RW-058'}</div>
              {data?.order_number && <div style={{ fontSize: '12px' }}>Order No: {data.order_number}</div>}
              {data?.job_site_address && <div style={{ fontSize: '12px' }}>Site Address: {data?.job_site_address}</div>}
            </td>
          </tr>
        </tbody>
      </table>
      <table style={{ width: '100%', marginTop: '15px', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#d17f15', color: 'white', padding: '8px', fontSize: '13px' }}>
            <th style={{ textAlign: 'left', padding: '8px' }}>Description</th>
            <th style={{ textAlign: 'center', padding: '8px' }}>Quantity</th>
            <th style={{ textAlign: 'center', padding: '8px' }}>Unit Price</th>
            <th style={{ textAlign: 'right', padding: '8px' }}>Cost</th>
          </tr>
        </thead>
        <tbody>
          {data?.items?.filter(item => item.item_name && item.price).map((item, index) => (
            <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f3f3f3' }}>
              <td style={{ padding: '8px', verticalAlign: 'top', border: '1px dotted #c0c0c0', fontSize: '13px' }}>
                {item.item_name || 'N/A'}
                {showDescription && (
                  <div
                    style={{ marginTop: '5px' }}
                    dangerouslySetInnerHTML={{
                      __html: item.description
                    }}
                  />
                )}
              </td>
              <td style={{ textAlign: 'center', padding: '8px', border: '1px dotted #c0c0c0', fontSize: '13px',paddingTop:item.description ? 20 : 0 }}>
                {item.quantity || 1}
              </td>
              <td style={{ textAlign: 'center', padding: '8px', border: '1px dotted #c0c0c0', fontSize: '13px',paddingTop:item.description ? 20 : 0 }}>
                £{parseFloat(item.unit_price || 0).toFixed(2)}
              </td>
              <td style={{ textAlign: 'right', padding: '8px', border: '1px dotted #c0c0c0', fontSize: '13px' ,paddingTop:item.description? 20: 0}}>
                £{parseFloat(item.price || 0).toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <table style={{ width: '100%', marginTop: '15px' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top', background: '#f2f2f2', border: '2px solid #d17f15', textAlign: 'center', padding: '10px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '13px' }}>Payment Instructions</div>
              <div style={{ fontSize: '12px' }}>{data?.company?.accountTitle || 'R-Wire Limited'}</div>
              <div style={{ fontSize: '12px' }}>Sort code: {data?.company?.sortCode || '09-01-29'}</div>
              <div style={{ fontSize: '12px' }}>Account no: {data?.company?.accountNumber || '35103808'}</div>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top', textAlign: 'right' }}>
              <div style={{ padding: '5px', background: '#f3f3f3', fontSize: '12px' }}>
                Total Net Amount: £{calculateSubtotal().toFixed(2)}
              </div>
              {data?.discount > 0 && (
                <div style={{ padding: '5px', fontSize: '12px' }}>
                  Discount: £{((calculateSubtotal() * (parseFloat(data?.discount) || 0)) / 100).toFixed(2)}
                </div>
              )}
              {data?.tax > 0 && (
                <div style={{ padding: '5px', fontSize: '12px' }}>
                  Total VAT Amount: £{calculateTax().toFixed(2)}
                </div>
              )}
              <div style={{ border: '1px solid #d17f15', margin: '5px 0' }}></div>
              <div style={{ fontWeight: 'bold', padding: '5px', fontSize: '13px' }}>
                Invoice Total: £{calculateTotal().toFixed(2)}
              </div>
              <div style={{ border: '1px solid #d17f15', margin: '5px 0' }}></div>
            </td>
          </tr>
        </tbody>
      </table>
      {showTerms && (
        <div style={{ textAlign: 'center', pageBreakBefore: 'always' }}>
          <div style={{ marginTop: '15px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '13px' }}>Terms and Conditions:</div>
            <div style={{ marginTop: '8px', fontSize: '12px' }}>
              <div><strong>Payment Due Date:</strong> Full payment is due within 14 days from the invoice date.</div>
              <div><strong>Late Payments:</strong> A late fee of 5% of the total invoice amount will be applied to any payment not received by the due date. Interest on overdue balances will accrue at 5% per week until payment is received in full.</div>
              <div><strong>Methods of Payment:</strong> Payment can be made via bank transfer. Please ensure the invoice number is included in all payments for proper processing.</div>
              <div><strong>Disputes:</strong> Any disputes regarding the invoice must be reported in writing within 7 days of the invoice date. Disputing any portion of the invoice does not suspend the obligation to pay the undisputed amount by the due date.</div>
              <div><strong>Suspension of Service:</strong> Non-payment may result in suspension of service or further legal action, at the discretion of R-Wire Limited.</div>
            </div>
            <div style={{ marginTop: '15px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '13px' }}>Disclaimer:</div>
              <div style={{ fontSize: '12px' }}>
                If payment of invoices is not received, we reserve the right to recover any cost, commissions or fees incurred for the collection of payments including those which may be incurred by using a debt collection agency. There will be a 5% late payment fee applied every 7 days after the due date of the invoice.
              </div>
            </div>
          </div>
          <div style={{ marginTop: '30px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{data?.company?.name || 'R-Wire Limited'}</div>
            <div style={{ fontSize: '12px' }}>Tel: {data?.company?.phone || '07950861019 / 07300400766'}</div>
            <div style={{ fontSize: '12px' }}>{convertDateFormat(data?.issue_date) || '2025-06-27'}</div>
          </div>
          <div style={{ display: 'flex', columnGap: '8px', justifyContent: 'center', borderTop: '1px solid #c0c0c0', paddingTop: '15px', marginTop: '20px' }}>
            {footerLogosBase64.main && (
              <div style={{ width: '100%' }}>
                <img  src={footerLogosBase64.main} alt="Logo 1" />
              </div>
            )}
            
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="container py-4">
      <style>
        {`
          .card {
            border-radius: 10px;
            box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
          }
          .card-header {
            background-color: #f8f9fa;
            border-bottom: 1px solid #dee2e6;
            padding: 1rem;
          }
          .btn {
            border-radius: 6px;
            padding: 0.4rem 0.8rem;
            font-weight: 500;
            transition: all 0.2s ease;
          }
          .btn-primary-600 {
            background-color: #007bff;
            border-color: #007bff;
          }
          .btn-primary-600:hover {
            background-color: #0056b3;
            border-color: #004085;
          }
          .btn-success {
            background-color: #28a745;
            border-color: #28a745;
          }
          .btn-success:hover {
            background-color: #1e7e34;
            border-color: #1c7430;
          }
          .btn-outline-primary {
            border-color: #007bff;
            color: #007bff;
          }
          .btn-outline-primary:hover {
            background-color: #007bff;
            color: white;
          }
          .btn-disabled {
            background-color: #6c757d;
            border-color: #6c757d;
            cursor: not-allowed;
          }
          .pdf-container {
            border: 1px solid #dee2e6;
            border-radius: 6px;
            overflow: hidden;
            background: white;
          }
          .alert {
            border-radius: 6px;
            padding: 0.8rem;
          }
          .modal-content {
            border-radius: 10px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          }
          .form-control, .form-select {
            border-radius: 6px;
            padding: 0.6rem;
          }
          .form-check {
            margin-bottom: 0.5rem;
          }
          .form-check-label {
            font-size: 0.9rem;
            margin-left: 0.5rem;
          }
          .radius-8 {
            border-radius: 6px !important;
          }
          td ol, td ul , td p {
            margin-bottom:0px
          }
          li[data-list="bullet"] {
            list-style: disc;
            margin-left:20px;
          }
        `}
      </style>
      <div className="card">
        <div className="card-header">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
            <div className="gap-2" style={{ display: 'flex', alignItems: 'flex-end' }}>
              <Link
                to="#"
                onClick={handleSendInvoiceClick}
                className="btn btn-sm btn-primary-600 radius-8 d-inline-flex align-items-center gap-1"
              >
                <Icon icon="pepicons-pencil:paper-plane" className="text-xl" />
                Send Invoice
              </Link>
              <Link
                to={`/invoices/edit/${data?.id}`}
                className="btn btn-sm btn-success radius-8 d-inline-flex align-items-center gap-1"
              >
                <Icon icon="uil:edit" className="text-xl" />
                Edit
              </Link>
                <PDFDownloadLink
                    document={
                      <InvoicePDF
                        data={data}
                        showLogo={showLogo}
                        showAddress={showAddress}
                        showPhone={showPhone}
                        showVAT={showVAT}
                        showDescription={showDescription}
                        showTerms={showTerms}
                        logoBase64={logoBase64}
                        footerLogosBase64={footerLogosBase64}
                      />
                    }
                    fileName={`Invoice-${data?.id || 'RW-058'}.pdf`}
                    className="btn btn-sm btn-outline-primary radius-8 d-inline-flex align-items-center gap-1"
                >
                  {({ loading }) => {
                    setSpinner(loading);
                    return loading ? 'Preparing PDF...' : (
                      <>
                        <Icon icon="mdi:download" className="text-xl" />
                        Download PDF
                      </>
                    );
                  }}
                </PDFDownloadLink>
            </div>
            <div className="d-flex flex-wrap gap-3 pt-10">
              <p>Show Items</p>
              <Form.Check
                type="checkbox"
                label="Company Logo"
                checked={showLogo}
                onChange={(e) => setShowLogo(e.target.checked)}
                className="form-check"
              />
              <Form.Check
                type="checkbox"
                label="Company Address"
                checked={showAddress}
                onChange={(e) => setShowAddress(e.target.checked)}
                className="form-check"
              />
              <Form.Check
                type="checkbox"
                label="Company Email"
                checked={showPhone}
                onChange={(e) => setShowPhone(e.target.checked)}
                className="form-check"
              />
              <Form.Check
                type="checkbox"
                label="Company VAT"
                checked={showVAT}
                onChange={(e) => setShowVAT(e.target.checked)}
                className="form-check"
              />
              <Form.Check
                type="checkbox"
                label="Item Description"
                checked={showDescription}
                onChange={(e) => setShowDescription(e.target.checked)}
                className="form-check"
              />
              <Form.Check
                type="checkbox"
                label="Terms and Conditions"
                checked={showTerms}
                onChange={(e) => setShowTerms(e.target.checked)}
                className="form-check"
              />
            </div>
          </div>
        </div>
        <div>
       
        </div>
        <div className="card-body py-3">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="pdf-container" style={{ maxHeight: '700px', overflowY: 'auto' }}>
                {renderInvoiceTemplate()}
              </div>
            </div>
          </div>
        </div>
      </div>
   
      {/* Modal for Send Invoice */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <h6>Send Invoice</h6>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="sendMethod" className="mb-3">
              <Form.Label>Send Method</Form.Label>
              <Form.Select
                value={sendMethod}
                onChange={(e) => setSendMethod(e.target.value)}
                className="radius-8"
              >
                <option value="email">Email</option>
              </Form.Select>
            </Form.Group>
            {sendMethod === 'email' ? (
              <Form.Group controlId="emailInput">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter email"
                  value={sendValue}
                  onChange={(e) => setSendValue(e.target.value)}
                  className="radius-8"
                />
              </Form.Group>
            ) : (
              <Form.Group controlId="phoneInput">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="tel"
                  placeholder="Enter phone number"
                  value={sendValue}
                  onChange={(e) => setSendValue(e.target.value)}
                  className="radius-8"
                />
              </Form.Group>
            )}
            {message.reason && (
              <div
                className={`mt-3 alert ${message.status ? 'alert-success' : 'alert-danger'}`}
              >
                <strong>{message.reason}:</strong> {message.desc}
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSend}>
            Send
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default InvoicePreviewLayer;